import { Link, Outlet, useNavigate, useRouter, useRouterState, type ErrorComponentProps } from '@tanstack/react-router';
import { Suspense, useEffect, useState } from 'react';
import { api } from '../account/api.ts';
import { rememberedUser } from '../account/access.ts';
import { refreshAccount, useAccount } from '../account/store.ts';
import { RealmClientError } from '../db/client.ts';
import { DiceRoller } from '../dice/DiceRoller.tsx';
import { HelpPanel, topicFor, type Topic } from '../help.tsx';
import { RealmRole } from './RealmRole.tsx';
import { useInstall } from '../install.ts';
import { useShortcut } from '../shortcuts.ts';
import { ThesaurusPanel } from '../thesaurus/ThesaurusPanel.tsx';
import { closeThesaurus, openThesaurus, useThesaurusPanel } from '../thesaurus/client.ts';

/** Which build this is: the web edition's version, and the commit it was built from. */
export const BUILD = `v${__APP_VERSION__} (${__APP_COMMIT__})`;

/**
 * The Dice Roller and help are for people using their realms: signed in, or offline on a
 * device that remembers its user. Signed out, the app shows only its welcome and account pages.
 */
function useToolsAllowed(): boolean {
  const account = useAccount();
  return account.status === 'signed-in' || (account.status === 'unavailable' && rememberedUser() !== null);
}

export function Shell() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();
  const tools = useToolsAllowed();
  // One panel beside the page at a time: this view's help, the Dice Roller, or the Thesaurus.
  const [help, setHelp] = useState<Topic | null>(null);
  const [dice, setDice] = useState(false);
  const words = useThesaurusPanel();
  const showHelp = (topic: Topic | null) => {
    setHelp(topic);
    if (topic) {
      setDice(false);
      closeThesaurus();
    }
  };
  const toggleDice = () => {
    setDice((open) => !open);
    setHelp(null);
    closeThesaurus();
  };
  // The Thesaurus opens from the editors (Shift+F7, a right-click) as well as here.
  useEffect(() => {
    if (!words.open) return;
    setHelp(null);
    setDice(false);
  }, [words.open, words.opened]);
  // F1 (app.help) opens this view's page, or closes the help; Ctrl+, (app.settings) opens Settings;
  // Ctrl+Shift+D (app.diceRoller) opens the Dice Roller.
  useShortcut('app.help', () => tools && showHelp(help ? null : topicFor(path)));
  useShortcut('app.settings', () => tools && void navigate({ to: '/settings' }));
  useShortcut('app.diceRoller', () => tools && toggleDice());
  // Another view, another page: the help follows the app.
  useEffect(() => setHelp((open) => (open ? topicFor(path) : null)), [path]);
  // Signed out: the panels close with the realms.
  useEffect(() => {
    if (tools) return;
    setHelp(null);
    setDice(false);
    closeThesaurus();
  }, [tools]);
  // The Player View's window shows the map alone, for a TV or second screen.
  if (path === '/player') return <Outlet />;
  return (
    <div className={help || dice || words.open ? 'shell with-help' : 'shell'}>
      <header className="app-bar">
        <Link to="/" className="brand">
          <img src="/favicon.png" alt="" width="24" height="24" />
          <span>Realmery</span>
          <span className="brand-edition">Web</span>
        </Link>
        <span className="app-bar-note">
          Preview · invite-only · <span title="The version, and the commit it was built from">{BUILD}</span>
        </span>
        {tools && <RealmRole />}
        <InstallButton />
        {tools && (
          <>
            <button
              type="button"
              className="app-bar-help"
              aria-expanded={words.open}
              title="Thesaurus: look up a word (in a scene or entry, Shift+F7 on a word, or right-click it)"
              onClick={() => (words.open ? closeThesaurus() : openThesaurus(null))}
            >
              <span aria-hidden="true">Aa</span>
              <span className="visually-hidden">Thesaurus</span>
            </button>
            <button type="button" className="app-bar-help" aria-expanded={dice} title="Dice Roller (Ctrl+Shift+D)" onClick={toggleDice}>
              <span aria-hidden="true">🎲</span>
              <span className="visually-hidden">Dice Roller</span>
            </button>
            <button
              type="button"
              className="app-bar-help"
              aria-expanded={help !== null}
              title="Help for this view (F1)"
              onClick={() => showHelp(help ? null : topicFor(path))}
            >
              ?<span className="visually-hidden"> Help</span>
            </button>
          </>
        )}
        <AccountLink />
      </header>
      <ServiceNotice />
      <main className="shell-main">
        <Suspense fallback={<p className="meta">Loading…</p>}>
          <Outlet />
        </Suspense>
      </main>
      {tools && help && <HelpPanel topic={help} onTopic={showHelp} onClose={() => setHelp(null)} />}
      {tools && dice && <DiceRoller onClose={() => setDice(false)} />}
      {tools && words.open && <ThesaurusPanel />}
    </div>
  );
}

function InstallButton() {
  const { canInstall, install } = useInstall();
  if (!canInstall) return null;
  return (
    <button type="button" className="app-bar-install" onClick={() => void install()}>
      Install app
    </button>
  );
}

interface ServiceConfig {
  maintenance?: boolean;
  banner?: { text: string; until: number | null } | null;
}

/**
 * The announcement banner and maintenance notice set on the admin site (docs/ADMIN-PLAN.md
 * 4.5): read on loading, when the page comes back to the front, and every 5 minutes.
 */
function ServiceNotice() {
  const [config, setConfig] = useState<ServiceConfig>({});
  useEffect(() => {
    const read = () => void api.get<ServiceConfig>('config').then(setConfig, () => undefined);
    read();
    const timer = setInterval(read, 5 * 60_000);
    const visible = () => document.visibilityState === 'visible' && read();
    document.addEventListener('visibilitychange', visible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', visible);
    };
  }, []);
  return (
    <>
      {config.maintenance && (
        <p className="service-notice service-maintenance" role="status">
          Realmery is down for maintenance: changes are kept on this device but can’t be saved to your account until it’s back.
        </p>
      )}
      {config.banner && (
        <p className="service-notice" role="status">
          {config.banner.text}
        </p>
      )}
    </>
  );
}

/** Pages anyone may visit; every other page needs an account (router.tsx). */
const PUBLIC_PATHS = new Set(['/welcome', '/signin', '/signup', '/recover', '/account', '/download', '/quickstart']);

function AccountLink() {
  const account = useAccount();
  const navigate = useNavigate();
  const path = useRouterState({ select: (state) => state.location.pathname });
  useEffect(() => {
    void refreshAccount().catch(() => undefined);
  }, []);
  // Signed out (here, in another tab's session list, or by the server): leave the realm pages.
  useEffect(() => {
    if (account.status === 'signed-out' && !PUBLIC_PATHS.has(path)) void navigate({ to: '/welcome', replace: true });
  }, [account.status, path, navigate]);

  if (account.status === 'signed-in') {
    return (
      <span className="app-bar-account-links">
        <Link to="/settings" className="app-bar-account">
          Settings
        </Link>
        <Link to="/account" className="app-bar-account">
          {account.me.user.email}
        </Link>
      </span>
    );
  }
  if (account.status === 'signed-out') {
    return (
      <span className="app-bar-account-links">
        <Link to="/signin" className="app-bar-account">
          Sign in
        </Link>
        <Link to="/signup" className="app-bar-account">
          Create account
        </Link>
      </span>
    );
  }
  if (account.status === 'unavailable') {
    const remembered = rememberedUser();
    if (remembered) return <span className="app-bar-account" title="Realmery can't be reached; your realms still work here.">{remembered.email} · offline</span>;
  }
  return null;
}

export function NotFound() {
  return (
    <section className="panel narrow">
      <h1>Not found</h1>
      <p>There is nothing at this address.</p>
      <Link to="/">Back to your realms</Link>
    </section>
  );
}

export function ErrorPanel({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  const message = error instanceof RealmClientError ? error.message : 'Something went wrong.';
  if (!(error instanceof RealmClientError)) console.error(error);
  return (
    <section className="panel narrow" role="alert">
      <h1>That did not work</h1>
      <p>{message}</p>
      <div className="actions">
        <button
          type="button"
          className="primary"
          onClick={() => {
            reset();
            void router.invalidate();
          }}
        >
          Try again
        </button>
        <Link to="/">Back to your realms</Link>
      </div>
    </section>
  );
}
